import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";

interface Project {
  id: string;
  name: string;
  description?: string;
  asin?: string;
  emoji?: string;
  data?: { main_image: { link: string } } | undefined;
  marketplace?: string | undefined;
  originalProjectId?: string | undefined;
  building?: boolean; // Ajouté pour gérer le loader de création
}

interface ProjectListContextProps {
  projects: Project[];
  loading: boolean;
  refreshProjects: () => Promise<void>;
  setProjects: React.Dispatch<React.SetStateAction<Project[]>>; // Ajout de setProjects
}

const ProjectListContext = createContext<ProjectListContextProps | undefined>(
  undefined,
);

export const useProjectList = () => {
  const context = useContext(ProjectListContext);
  if (!context) {
    throw new Error("useProjectList must be used within a ProjectListProvider");
  }
  return context;
};

export const ProjectListProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { currentUser, getToken } = useAuth();

  const refreshProjects = useCallback(async () => {
    if (!currentUser) return;

    // Ne pas remettre loading à true ici si on veut juste rafraîchir silencieusement
    // Mais pour le premier chargement c'est important.
    // On peut gérer le loading initial via useEffect distinct ou ici si projects est vide.
    
    const token = await getToken();
    try {
      const response = await fetch(`/.netlify/functions/getProjects`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userId: currentUser.uid }),
      });

      if (!response.ok) {
        throw new Error("Failed to refresh projects");
      }

      const data = await response.json();
      setProjects([...data.projects]);
    } catch (error) {
      console.error("Error refreshing projects:", error);
    } finally {
      setLoading(false);
    }
  }, [currentUser, getToken]);

  useEffect(() => {
    if (currentUser) {
      refreshProjects();
    }
  }, [currentUser, refreshProjects]);

  return (
    <ProjectListContext.Provider
      value={{ projects, loading, setProjects, refreshProjects }}
    >
      {children}
    </ProjectListContext.Provider>
  );
};
